import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AuthContext, IntlContext } from "context";
import { Plus } from "lucide-react";
import { fetchServices, serviceKeys, updateService, useServiceMinSalary } from "hooks/service";
import { useStripeAccount } from "hooks/stripe";
import { ServiceInterface } from "types/service";
import { ROUTES, buildRoute } from "routes";
import { Button } from "components/ui/button";
import { Loader } from "ui/Loader";
import { isServiceEffectivelyActive, PROFILE_COMPLETION_SECTION_ID } from "../../profileCompletion";
import { useProviderProfileCompletion } from "../../useProviderProfileCompletion";
import ProviderStandbyBanner from "./ProviderStandbyBanner";
import ServiceCard from "./ServiceCard";

const MesServicesList: React.FC = () => {
    const intl = useContext(IntlContext);
    const { account, updateAccount } = useContext(AuthContext);
    const provider = account?.resources?.provider?.[0];
    const { data: stripeAccount, isLoading: stripeLoading } = useStripeAccount(provider?.stripe_id);
    const { data: minSalary = 0 } = useServiceMinSalary();
    const profileCompletion = useProviderProfileCompletion();
    const profileComplete = profileCompletion?.requiredComplete ?? false;
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);

    const {
        data: services = [],
        isLoading,
        refetch,
    } = useQuery({
        queryKey: serviceKeys.lists(),
        queryFn: () => fetchServices(),
    });

    const serviceList = services as ServiceInterface[];
    const hasActiveService = useMemo(
        () => serviceList.some((service) => isServiceEffectivelyActive(service.is_active, profileComplete)),
        [serviceList, profileComplete]
    );

    const activeCount = useMemo(
        () =>
            serviceList.filter((service) =>
                isServiceEffectivelyActive(service.is_active, profileComplete)
            ).length,
        [serviceList, profileComplete]
    );

    const handleSaleStatusChange = async (isOnSale: boolean) => {
        const servicesToUpdate = serviceList.filter((service) =>
            isOnSale ? !service.is_active : service.is_active
        );

        if (servicesToUpdate.length === 0) {
            return;
        }

        setIsBulkUpdating(true);
        try {
            await Promise.all(
                servicesToUpdate.map((service) =>
                    updateService(service.id, { ...service, is_active: isOnSale })
                )
            );
            await refetch();
            await updateAccount?.();
        } finally {
            setIsBulkUpdating(false);
        }
    };

    if ((isLoading && serviceList.length === 0) || (stripeLoading && !stripeAccount)) {
        return <Loader />;
    }

    const showCesuReminder =
        !provider?.num_cesu &&
        provider?.provider_services &&
        provider.provider_services.length > 0;

    const showStripeReminder = stripeAccount?.capabilities.transfers !== "active";
    const showProfileCompletionReminder = Boolean(profileCompletion && !profileComplete);

    return (
        <div className="rounded-2xl bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Mes services</h2>
                    <p className="mt-1 text-sm text-slate-500">
                        {activeCount} service{activeCount > 1 ? "s" : ""} actif{activeCount > 1 ? "s" : ""}
                    </p>
                </div>
                <Button asChild className="rounded-xl">
                    <Link to={ROUTES.MY_SERVICES_CREATE}>
                        <Plus className="h-4 w-4" />
                        {intl.formatMessage("service.label.add")}
                    </Link>
                </Button>
            </div>

            {serviceList.length > 0 && profileComplete ? (
                <ProviderStandbyBanner
                    isOnSale={hasActiveService}
                    disabled={isBulkUpdating}
                    onIsOnSaleChange={(isOnSale) => {
                        void handleSaleStatusChange(isOnSale);
                    }}
                />
            ) : null}

            {(showCesuReminder ||
                showStripeReminder ||
                showProfileCompletionReminder) && (
                <div className="mb-6 space-y-3">
                    {showProfileCompletionReminder && (
                        <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            Vos services restent inactifs tant que les informations obligatoires de votre profil ne sont pas renseignées.{" "}
                            <Link
                                to={`${ROUTES.ACCOUNT_PROFILE}#${PROFILE_COMPLETION_SECTION_ID}`}
                                className="font-semibold text-primary hover:underline"
                            >
                                Compléter mon profil
                            </Link>
                            .
                        </p>
                    )}
                    {showCesuReminder && (
                        <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            Vous avez reçu votre n°CESU salarié ? Indiquez-le dans{" "}
                            <Link to={ROUTES.ACCOUNT_PROFILE} className="font-semibold text-primary hover:underline">
                                votre profil
                            </Link>
                            .
                        </p>
                    )}
                    {showStripeReminder && !provider?.stripe_id && (
                        <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            Finalisez vos coordonnées bancaires dans{" "}
                            <Link to={ROUTES.ACCOUNT_PROFILE} className="font-semibold text-primary hover:underline">
                                votre profil
                            </Link>{" "}
                            pour publier vos services.
                        </p>
                    )}
                    {showStripeReminder && provider?.stripe_id && (
                        <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            Mettez à jour votre compte{" "}
                            <Link
                                to={buildRoute.stripeRedirect(String(provider.stripe_id))}
                                className="font-semibold text-primary hover:underline"
                            >
                                Stripe Connect
                            </Link>{" "}
                            pour publier vos services.
                        </p>
                    )}
                </div>
            )}

            {serviceList.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-slate-50 px-6 py-12 text-center">
                    <p className="text-sm text-slate-600">
                        {intl.formatMessage("profile.label.my_profile.no_service")}
                    </p>
                    <Button asChild className="mt-4 rounded-xl">
                        <Link to={ROUTES.MY_SERVICES_CREATE}>{intl.formatMessage("service.label.add")}</Link>
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    {serviceList.map((service) => (
                        <ServiceCard
                            key={service.id}
                            service={service}
                            minSalary={minSalary}
                            profileComplete={profileComplete}
                            onUpdated={() => void refetch()}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default MesServicesList;
